import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, List, RawButton, Text } from '@common-fe/common-fe';
import _ from 'lodash';
import styled from 'styled-components';

import { ROUTES } from '@/common';
import Permissions from '@/common/permissions';
import { useHasAccess, useHistory } from '@/modules/core/hooks';

import { NO_ACCESS_ROLE_ID } from '../role.types';
import { useRoleSetupStore } from '../RoleSetup/stores';
import { useHighlightNewRoleStore } from '../stores';

import { RolesListItemPayload } from './RolesList.types';

interface Props extends RolesListItemPayload {
  onOpenSummary: (id: string) => void;
  onSetCopyModal: (id: string) => void;
}

const StyledText = styled(Text)`
  &:hover {
    color: ${({ theme }) => theme.colors.textAccent};
  }
`;

const TIMEOUT = 3500;

const RolesListItem: React.FC<Props> = ({
  id,
  name,
  description,
  onSetCopyModal,
  level,
  onOpenSummary,
  organizationName,
}) => {
  const isAvailableToEdit = useHasAccess([
    {
      permission: Permissions.EDIT_ROLES_AND_SECURITY,
    },
  ]);
  const history = useHistory();
  const highlightedId = useHighlightNewRoleStore((state) => state.highlightId);
  const setHighlightedId = useHighlightNewRoleStore((state) => state.setHighlightId);
  const handleReset = useRoleSetupStore((state) => state.handleReset);

  const formattedLevel = useMemo(() => {
    return _.capitalize(level);
  }, [level]);
  useEffect(() => {
    if (highlightedId === id) {
      setTimeout(() => {
        setHighlightedId('');
      }, TIMEOUT);
    }
  }, [highlightedId, id, setHighlightedId]);

  const handleOpenSummary = useCallback(() => {
    handleReset();
    onOpenSummary(id);
  }, [id, onOpenSummary, handleReset]);

  const handleOpenCopyRole = useCallback(() => {
    onSetCopyModal(id);
  }, [id, onSetCopyModal]);

  const handleOpenEdit = useCallback(() => {
    handleReset();
    history.push(ROUTES.ROLE_EDIT(id));
  }, [history, id, handleReset]);

  return (
    <List.Item onClick={handleOpenSummary} testId="roles-list_item" isHighlighted={highlightedId === id}>
      <Box direction="column" style={{ flex: 1 }}>
        <RawButton onClick={handleOpenSummary} style={{maxWidth: '140px', display: 'block'}}>
          <Box>
            <StyledText truncate title={name} >
              {name}
            </StyledText>
        
            <List.Item.Text size="small" color="textSecondary" truncate title={description}>
              {description}
            </List.Item.Text>
          </Box>
        </RawButton>
      </Box>

      <Box align="center" direction="row" style={{ flex: 3 }}>
        <Box align="center" round="snackbarRound" background="border1" pad={{ horizontal: 'xxs' }}>
          <Text size="xsmall" style={{ lineHeight: '18px' }} color="textBody">
            {formattedLevel}
          </Text>
        </Box>
        <Box margin={{ left: 'xs' }}>
          <Text weight={400}>{organizationName}</Text>
        </Box>
      </Box>

      <List.Item.Options testId="role_options_button">
        <List.Item.Options.Item testId="role_options_details_button" onClick={handleOpenSummary}>View summary</List.Item.Options.Item>
        {isAvailableToEdit && (
          <List.Item.Options.Item testId="role_options_edit_button" onClick={handleOpenEdit}>
            Edit
          </List.Item.Options.Item>
        )}

        {isAvailableToEdit && id !== NO_ACCESS_ROLE_ID && (
          <List.Item.Options.Item onClick={handleOpenCopyRole}>
            Copy permissions
          </List.Item.Options.Item>

        )}
      </List.Item.Options>
    </List.Item>
  );
};

export default RolesListItem;
